import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { FilterGroupingEnum, SiteArea } from 'graphql-common';
import Typography from '@lib/components/Typography/Typography';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import { materialSymbolsOutlined } from '@lib/enums/common';
import TextEditor from '@lib/components/TextEditor/TextEditor';
import { hasTextEditorValue } from '@lib/components/TextEditor/utils';
import entityViewStyles from '@lib/assets/styles/EntityView.module.scss';
import LinkedDocuments from '@lib/components/LinkedDocuments';
import { APP_URLS } from 'constants/urls';

interface Props {
  areaData?: SiteArea;
  isDocumentationLocked?: boolean;
  onEdit: () => void;
}

function AreaView(props: Props) {
  const { t } = useTranslation();
  const { areaData, isDocumentationLocked, onEdit } = props;
  const { id, code, description, address } = areaData || {};
  return (
    <>
      <div className={entityViewStyles.infoList}>
        <div className={entityViewStyles.infoItem}>
          <p className={entityViewStyles.infoItemLabel}>
            <MaterialIcon icon="password" symbolsOutlined />
            <Typography variant="caption">{t('code')}</Typography>
          </p>
          <div className={entityViewStyles.infoItemContent}>
            <Typography variant="body">{code}</Typography>
          </div>
        </div>
        <div className={entityViewStyles.infoItem}>
          <p className={entityViewStyles.infoItemLabel}>
            <MaterialIcon icon="distance" symbolsOutlined />
            <Typography variant="caption">{t('address')}</Typography>
          </p>
          <div className={entityViewStyles.infoItemContent}>
            <Typography variant="body">{address}</Typography>
          </div>
        </div>
        {hasTextEditorValue(description) && (
          <div
            className={classNames(
              entityViewStyles.infoItem,
              entityViewStyles.block,
            )}
          >
            <p className={entityViewStyles.infoItemLabel}>
              <MaterialIcon icon="description" symbolsOutlined />
              <Typography variant="caption">{t('description')}</Typography>
            </p>
            <div className={entityViewStyles.infoItemContent}>
              <TextEditor
                readOnly
                value={description}
                className={entityViewStyles.description}
              />
            </div>
          </div>
        )}
        {id && !isDocumentationLocked && (
          <div
            className={classNames(
              entityViewStyles.infoItem,
              entityViewStyles.block,
            )}
          >
            <p className={entityViewStyles.infoItemLabel}>
              <MaterialIcon icon="attach_file" symbolsOutlined />
              <Typography variant="caption">
                {t('area-related-documents')}
              </Typography>
            </p>
            <div className={entityViewStyles.infoItemContentFullWidth}>
              <LinkedDocuments
                filters={[
                  {
                    linkedAreasId: {
                      grouping: FilterGroupingEnum.And,
                      predicate: { in: [id] },
                    },
                  },
                ]}
                getDetailsUrl={(doc) =>
                  APP_URLS.dashboard.documentation.view.getDynamicPath({
                    pathParts: { id: doc.id },
                  })
                }
              />
            </div>
          </div>
        )}
      </div>
      <Button
        buttonText={t('edit-site-area')}
        buttonType={ButtonTypes.primaryOutlined}
        leftIcon="edit"
        leftIconClassName={materialSymbolsOutlined}
        onClick={onEdit}
        fullWidth
      />
    </>
  );
}

export default AreaView;
